import { skyCathedralCameraFrame } from './242-sky-cathedral-framing.mjs';
import { skyOrbitalCameraFrame } from './119-sky-camera.mjs';
import { skyProjectileContact } from '../../../packages/domain/src/80-sky-aim.mjs';
import {
  SKY_WORLD,
  SKY_WORLD_ASSETS,
  SKY_LEVIATHAN_SPACE,
} from '../../../packages/domain/src/86-sky-world-scale.mjs';
import {
  skyFlightFrame,
  skyFlightPointToWorld,
  skyFlightPointToLocal,
} from '../../../packages/domain/src/92-sky-flight-frame.mjs';
import { skyFlightFrameFromTangent } from '../../../packages/domain/src/92-sky-flight-frame.mjs';
import {
  skyLeviathanRouteFrame,
  skyLeviathanBoxContact,
} from '../../../packages/domain/src/95-sky-leviathan-space.mjs';
import { skyInteriorCameraFrame, SKY_OBSERVER_NEAR } from './161-sky-interior-camera.mjs';
import { skyTerrainHeight } from '../../../packages/domain/src/87-sky-terrain.mjs';
import { SKY_LEVIATHAN_POLICY } from '../../../packages/domain/src/93-sky-leviathan.mjs';
import { skyLeviathanArmorFrames } from '../../../packages/domain/src/97-sky-leviathan-armor.mjs';

const point = (x, y, z) => ({ x, y, z });
const smooth = (value) => {
  const t = Math.max(0, Math.min(1, value));
  return t * t * (3 - 2 * t);
};
const interior = new Set(['entry', 'throat', 'chamber', 'breakout']);
const armorBounds = new WeakMap();

/** Derive seven local mesh bounds from the accepted immutable collision handle
 * during asset preparation. Weak ownership follows that handle; no copied art
 * or contact surface persists beyond cache disposal. Unaccepted handles return
 * false. Calls during rendering reuse56 corners, never traverse109,990 faces. */
export function prepareSkyLeviathanReveal(handle) {
  if (armorBounds.has(handle)) return true;
  const identity = {
    origin: point(0, 0, 0),
    right: point(1, 0, 0),
    up: point(0, 1, 0),
    back: point(0, 0, 1),
  };
  if (!skyLeviathanArmorFrames(handle, identity)) return false;
  armorBounds.set(
    handle,
    handle.parts.map((part) => {
      const min = point(Infinity, Infinity, Infinity),
        max = point(-Infinity, -Infinity, -Infinity);
      for (const triangle of part.triangles)
        for (const p of [triangle.a, triangle.b, triangle.c])
          for (const axis of ['x', 'y', 'z']) {
            min[axis] = Math.min(min[axis], p[axis]);
            max[axis] = Math.max(max[axis], p[axis]);
          }
      const corners = [];
      for (const x of [min.x, max.x])
        for (const y of [min.y, max.y])
          for (const z of [min.z, max.z]) corners.push(point(x, y, z));
      return { id: part.id, corners };
    }),
  );
  return true;
}

function revealEnvelope(state, hull) {
  const handle = state.leviathanCollision;
  const frames = handle && skyLeviathanArmorFrames(handle, hull, state.leviathan.articulation);
  if (frames && prepareSkyLeviathanReveal(handle)) {
    return armorBounds
      .get(handle)
      .flatMap(({ id, corners }) =>
        corners.map((p) => skyFlightPointToLocal(hull, skyFlightPointToWorld(frames[id], p))),
      );
  }
  const asset = SKY_WORLD_ASSETS.gildedLeviathan,
    points = [];
  for (const x of [-SKY_LEVIATHAN_SPACE.finSpan / 2, SKY_LEVIATHAN_SPACE.finSpan / 2])
    for (const y of [-asset.height / 2 - 1, asset.height / 2 + 1])
      for (const z of [-asset.length / 2 - 2, asset.length / 2 + 2]) points.push(point(x, y, z));
  return points;
}

/** Close first person looks at the face, never at the user's aim point. The
 * crest remains a separate visible target above the mouth-centred view; moving
 * the cursor rotates only the actual cannon, not the observer camera. */
export function skyClosePilotCamera(state, observer, aspect) {
  if (!observer) return null;
  const mouth = state.boss?.attackSocket,
    crest = state.boss?.weakpoint;
  const target =
    mouth && crest
      ? point((mouth.x + crest.x) / 2, (mouth.y + crest.y) / 2, (mouth.z + crest.z) / 2)
      : point(observer.eye.x, observer.eye.y, observer.eye.z - 10);
  const basis = skyFlightFrameFromTangent(
    point(state.x, state.y, state.pilotZ ?? 1),
    point(0, 0, -1),
  );
  const frame = skyInteriorCameraFrame({
    flightFrame: basis,
    pilot: basis.origin,
    observer,
    thirdPerson: {
      ...observer.eye,
      lookX: target.x,
      lookY: target.y,
      lookZ: target.z,
      fov: 76,
      near: SKY_OBSERVER_NEAR,
    },
    aim: target,
    aspect,
    progress: 1,
  });
  return frame ? { ...frame, fov: 72 } : null;
}

// Pick the nearest actual surface on the bounded camera segment. This changes
// the requested endpoint only; the cannon still fires from its own barrel and
// its separate physical sweep can hit an intervening surface.
function visibleAimSurface(from, to, state, basis) {
  let nearest = null,
    distance = Infinity;
  for (const target of [
    ...(state.enemies ?? []),
    ...(state.boss?.authoredProps ?? []),
    ...(state.boss ? [state.boss] : []),
  ]) {
    if (!(target.hp > 0) || target.defeated || target.collisionEnabled === false) continue;
    const hit = skyProjectileContact(target, from, to, target.contactFrame ?? basis);
    if (!hit) continue;
    const x = hit.x - from.x,
      y = hit.y - from.y,
      z = hit.z - from.z;
    if (x * (to.x - from.x) + y * (to.y - from.y) + z * (to.z - from.z) <= 0) continue;
    const squared = x * x + y * y + z * z;
    if (squared < distance) {
      nearest = { x: hit.x, y: hit.y, z: hit.z };
      distance = squared;
    }
  }
  return nearest;
}

/** Convert canvas NDC to the nearest authored source surface on the bounded
 * camera ray, falling back to the current target-depth plane on a miss. Invalid
 * or rearward rays return null. Camera heading is unchanged; domain80 resolves
 * the independent barrel ray against intervening physical armor/apertures. */
export function skyCameraAimPoint(T, camera, ndc, state) {
  if (![ndc?.x, ndc?.y].every(Number.isFinite)) return null;
  camera.updateMatrixWorld(true);
  const direction = new T.Vector3(
    Math.max(-0.98, Math.min(0.98, ndc.x)),
    Math.max(-0.98, Math.min(0.98, ndc.y)),
    0.5,
  )
    .unproject(camera)
    .sub(camera.position)
    .normalize();
  const basis = skyFlightFrame(state.flightFrame);
  if (basis && Number.isFinite(state.flightAimDepth)) {
    const depth = Math.max(0.1, Math.min(130, state.flightAimDepth));
    const plane = skyFlightPointToWorld(
      { ...basis, origin: point(state.x, state.y, state.pilotZ) },
      point(0, 0, -depth),
    );
    const normal = new T.Vector3(-basis.back.x, -basis.back.y, -basis.back.z);
    const denominator = direction.dot(normal);
    if (denominator <= 0.001) return null;
    const travel =
      new T.Vector3(plane.x, plane.y, plane.z).sub(camera.position).dot(normal) / denominator;
    if (!(travel > 0)) return null;
    const far = {
      x: camera.position.x + direction.x * travel,
      y: camera.position.y + direction.y * travel,
      z: camera.position.z + direction.z * travel,
    };
    // Exact surface under the requested pixel, not acquisition/aim assistance.
    // A far plane alone makes a close small lens miss from barrel parallax.
    return visibleAimSurface(camera.position, far, state, basis) ?? far;
  }
  if (direction.z >= -0.001) return null;
  const depth = Math.min(
    camera.position.z - 0.1,
    state.boss?.weakpoint?.z ?? camera.position.z - 34,
  );
  const travel = (depth - camera.position.z) / direction.z;
  const far = {
    x: camera.position.x + direction.x * travel,
    y: camera.position.y + direction.y * travel,
    z: depth,
  };
  return state.boss?.authoredRig
    ? (visibleAimSurface(camera.position, far, state, null) ?? far)
    : far;
}

/** A framed pilot's canonical XYZ is its aiming/control point, not its feet.
 * Rotate the same metre root offset and forward Flight pitch with the physical
 * right/up/back basis. Invalid frames leave the ordinary renderer untouched. */
export function skyFramedPilotPose(state) {
  const basis = skyFlightFrame(state.flightFrame);
  if (!basis || ![state.x, state.y, state.pilotZ].every(Number.isFinite)) return null;
  return {
    basis,
    position: skyFlightPointToWorld(
      { ...basis, origin: point(state.x, state.y, state.pilotZ) },
      point(0, SKY_WORLD.pilotSoleOffset - SKY_WORLD.pilotAimHeight, 0),
    ),
    pitch: state.locomotion === 'fly' ? -0.08 : 0,
  };
}

/** Exact route history, not a tangent chord through the inside of a bend.
 * The physical pilot's u/v steering is retained at s−4 metres. Exterior views
 * use the same four-metre distance in the current flight frame. */
export function skyFramedHistoryCamera(state, aspect) {
  const basis = skyFlightFrame(state.flightFrame);
  if (!basis) return null;
  const live = state.leviathan,
    phase = live?.encounter?.phase ?? live?.phase;
  const turning = phase === 'breakout' && live?.systems?.woundTurn > 0;
  const history =
    live && interior.has(phase) && !turning
      ? skyLeviathanRouteFrame(live.hullFrame, live.route.s - 4, live.route.u, live.route.v)
      : { ...basis, origin: skyFlightPointToWorld(basis, point(0, 0, 4)) };
  const position = skyFlightPointToWorld(history, point(0.45, 0.85, 0));
  const target = skyFlightPointToWorld(basis, point(0, 0, -12));
  return {
    ...position,
    lookX: target.x,
    lookY: target.y,
    lookZ: target.z,
    up: { ...basis.up },
    fov: aspect < 1 ? 58 : 56,
    near: 0.15,
    maskWeight: 0,
    firstPerson: false,
  };
}

function shellClear(state, camera, aspect) {
  const live = state.leviathan;
  if (!live) return true;
  const local = skyFlightPointToLocal(live.hullFrame, camera);
  const near = camera.near ?? 0.15;
  const radius =
    near * Math.sqrt(1 + Math.tan((camera.fov * Math.PI) / 360) ** 2 * (1 + aspect ** 2));
  return ![...live.walls, ...live.machinery.flatMap((gate) => gate.boxes)].some((box) =>
    skyLeviathanBoxContact(local, radius, box),
  );
}

/** One protected three-quarter reveal in world metres. The lens travels around
 * the outside of the hull, fits its canonical full fin/length envelope at the
 * hero beat, and returns to the real history POV before controls open. The
 * polynomial has zero first/second endpoint derivatives; no camera clock, model
 * scaling or combat geometry changes. Reduced motion retains the ordinary POV. */
export function skyLeviathanArrivalCamera(state, aspect, history, reduced = false) {
  const live = state.leviathan,
    hull = skyFlightFrame(live?.hullFrame),
    age = live?.encounter?.phaseAge;
  if (
    reduced ||
    live?.encounter?.phase !== 'arrival' ||
    !live.systems?.protected ||
    !hull ||
    !history ||
    !Number.isFinite(aspect) ||
    aspect <= 0 ||
    !Number.isFinite(age) ||
    age <= 0 ||
    age >= SKY_LEVIATHAN_POLICY.arrivalSeconds
  )
    return history;
  const t = age / SKY_LEVIATHAN_POLICY.arrivalSeconds,
    weight = 64 * t ** 3 * (1 - t) ** 3,
    yaw = aspect < 1 ? 0.75 : 1.15,
    pitch = aspect < 1 ? 0.28 : 0.15,
    back = point(Math.sin(yaw) * Math.cos(pitch), Math.sin(pitch), Math.cos(yaw) * Math.cos(pitch)),
    right = point(Math.cos(yaw), 0, -Math.sin(yaw)),
    up = point(-Math.sin(yaw) * Math.sin(pitch), Math.cos(pitch), -Math.cos(yaw) * Math.sin(pitch)),
    fov = aspect < 1 ? 58 : 52,
    tangent = Math.tan((fov * Math.PI) / 360),
    dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
  let distance = 1;
  for (const offset of revealEnvelope(state, hull))
    distance = Math.max(
      distance,
      dot(offset, back) +
        Math.max(
          Math.abs(dot(offset, right)) / (tangent * aspect * 0.86),
          Math.abs(dot(offset, up)) / (tangent * 0.82),
        ),
    );
  const local = skyFlightPointToLocal(hull, history),
    startYaw = Math.atan2(local.x, local.z),
    bearing = startYaw + (yaw - startYaw) * weight,
    radius = Math.hypot(local.x, local.z) * (1 - weight) + distance * Math.cos(pitch) * weight,
    eye = skyFlightPointToWorld(
      hull,
      point(
        Math.sin(bearing) * radius,
        local.y * (1 - weight) + back.y * distance * weight,
        Math.cos(bearing) * radius,
      ),
    );
  return {
    ...history,
    ...eye,
    lookX: history.lookX * (1 - weight) + hull.origin.x * weight,
    lookY: history.lookY * (1 - weight) + hull.origin.y * weight,
    lookZ: history.lookZ * (1 - weight) + hull.origin.z * weight,
    fov: history.fov + (fov - history.fov) * weight,
  };
}

/** Protected collapse overview in world metres. Fit the physical hull, folded
 * supports and ground together; a small pullback changes only the lens position.
 * The local pilot and opaque wreck retain their real size and coordinates. */
export function skyLeviathanCrashCamera(state, aspect, reduced = false) {
  const live = state.leviathan,
    hull = skyFlightFrame(live?.hullFrame);
  if (!hull || !Number.isFinite(aspect) || aspect <= 0) return null;
  const asset = SKY_WORLD_ASSETS.gildedLeviathan,
    points = [];
  for (const x of [-SKY_LEVIATHAN_SPACE.finSpan / 2, SKY_LEVIATHAN_SPACE.finSpan / 2])
    for (const y of [-asset.height / 2, asset.height / 2])
      for (const z of [-asset.length / 2 - 2, asset.length / 2 + 2])
        points.push(skyFlightPointToWorld(hull, point(x, y, z)));
  for (const support of live.encounter?.geometry?.crashProbes ?? [])
    points.push(skyFlightPointToWorld(hull, support));
  const ground = skyTerrainHeight(hull.origin.x, hull.origin.z, state.terrainTravel);
  const target = point(hull.origin.x, (hull.origin.y + ground) / 2, hull.origin.z);
  for (const x of [-asset.width / 2, asset.width / 2])
    for (const z of [-asset.length / 2, asset.length / 2]) {
      const at = skyFlightPointToWorld(hull, point(x, 0, z));
      points.push(point(at.x, skyTerrainHeight(at.x, at.z, state.terrainTravel), at.z));
    }
  const back = { x: 0.8, y: 0.6, z: 1 },
    magnitude = Math.hypot(back.x, back.y, back.z);
  for (const axis of ['x', 'y', 'z']) back[axis] /= magnitude;
  const horizontal = Math.hypot(back.x, back.z),
    right = point(back.z / horizontal, 0, -back.x / horizontal),
    // back × right matches the renderer's world-up lookAt basis.
    up = point(back.y * right.z, back.z * right.x - back.x * right.z, -back.y * right.x);
  const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
  const fov = aspect < 1 ? 58 : 48,
    tangent = Math.tan((fov * Math.PI) / 360);
  let distance = 1;
  for (const at of points) {
    const offset = point(at.x - target.x, at.y - target.y, at.z - target.z);
    distance = Math.max(
      distance,
      dot(offset, back) +
        Math.max(
          Math.abs(dot(offset, right)) / (tangent * aspect * 0.86),
          Math.abs(dot(offset, up)) / (tangent * 0.82),
        ),
    );
  }
  distance *= 1 + 0.06 * (reduced ? 1 : smooth((live.encounter?.phaseAge ?? 0) / 2));
  return {
    x: target.x + back.x * distance,
    y: target.y + back.y * distance,
    z: target.z + back.z * distance,
    lookX: target.x,
    lookY: target.y,
    lookZ: target.z,
    up: point(0, 1, 0),
    fov,
    near: 0.15,
    maskWeight: 0,
    firstPerson: false,
  };
}

/** Renderer-owned camera option only: domain objectives never depend on POV.
 * Keep the reviewed history rail by default. First person requires explicit
 * acceptance of the actual pilot/portal blend; it enters/exits only on straight
 * route extensions, never halfway through the four-metre quarter-circle bend.
 * Finite endings and exterior phases restore the complete local helmet. */
export function skyFramedView(
  state,
  { aspect, aim, observer, reduced = false, firstPerson = false, blendAccepted = false } = {},
) {
  if (
    state.cathedral &&
    Number.isInteger(state.sector) &&
    state.cathedral.sector === state.sector
  ) {
    const cathedral = skyCathedralCameraFrame(state, aspect);
    if (cathedral) return cathedral;
  }
  if (state.orbitalFlight) return skyOrbitalCameraFrame(state, aspect, aim);
  if ((state.leviathan?.encounter?.phase ?? state.leviathan?.phase) === 'crash')
    return skyLeviathanCrashCamera(state, aspect, reduced);
  const history = skyFramedHistoryCamera(state, aspect);
  if (!history) return null;
  const live = state.leviathan,
    phase = live?.encounter?.phase ?? live?.phase;
  if (phase === 'arrival') return skyLeviathanArrivalCamera(state, aspect, history, reduced);
  if (
    !live ||
    !(interior.has(phase) || phase === 'wound') ||
    !['playing', 'paused'].includes(state.status) ||
    state.presentationPose ||
    !firstPerson ||
    !blendAccepted
  )
    return history;
  const s = live.route.s;
  const entry = smooth(s / 4);
  const exit = smooth((SKY_LEVIATHAN_SPACE.routeLength - s) / 4);
  // The protected exit turn rotates the actual pilot basis before controls
  // return. Finish at that same inward eye; never switch cameras on phase name
  // while the pilot is still facing away from the newly exposed wound.
  const turn = phase === 'breakout' ? (live.systems?.woundTurn ?? 0) : 0;
  return (
    skyInteriorCameraFrame({
      flightFrame: state.flightFrame,
      pilot: point(state.x, state.y, state.pilotZ),
      observer,
      thirdPerson: history,
      aim: observer?.eye
        ? skyFlightPointToWorld(
            { ...state.flightFrame, origin: observer.eye },
            point(0, 0, -(state.flightAimDepth ?? 96)),
          )
        : aim,
      aspect,
      progress: phase === 'wound' ? 1 : turn > 0 ? turn : Math.min(entry, exit),
      blendAllowed: true,
      reduced,
      isClear: (camera) => shellClear(state, camera, aspect),
    }) ?? history
  );
}

/** Reuse one basis matrix/quaternion per renderer; no model scale or physics
 * changes. Calling place before animation makes gun/eye queries current. */
export function createSkyFramedPlacement(T) {
  const matrix = new T.Matrix4(),
    localPitch = new T.Quaternion(),
    axis = new T.Vector3(1, 0, 0);
  return (rider, state) => {
    const pose = skyFramedPilotPose(state);
    if (!pose) return false;
    const { right, up, back } = pose.basis;
    matrix.set(
      right.x,
      up.x,
      back.x,
      0,
      right.y,
      up.y,
      back.y,
      0,
      right.z,
      up.z,
      back.z,
      0,
      0,
      0,
      0,
      1,
    );
    rider.position.set(pose.position.x, pose.position.y, pose.position.z);
    rider.quaternion
      .setFromRotationMatrix(matrix)
      .multiply(localPitch.setFromAxisAngle(axis, pose.pitch));
    rider.updateWorldMatrix(true, true);
    return true;
  };
}
